import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge, Box, Button, Dialog, alpha, DialogContent, DialogTitle, IconButton, ListItemText, Menu, MenuItem, Paper, Skeleton, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { AutoAwesomeRounded, Close, HistoryRounded, MoreHoriz, ReportProblemOutlined, EditOutlined, EventBusy } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { formatDistanceToNow } from 'date-fns';
import { useSessionContext } from '../../context/SessionContext';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { timetableService, Entry, Lesson, PrintView } from '../../services/timetableService';
import EmptyState from '../../components/common/EmptyState';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import { ToneChip } from '../../components/common/StatusChip';
import WeekGrid, { GridItem } from '../../components/timetable/WeekGrid';
import LessonCard from '../../components/timetable/LessonCard';
import ProblemList from '../../components/timetable/ProblemList';
import { ROOM_TYPE_LABELS } from '../../components/timetable/timetableUtils';
import { useBoard } from '../../components/timetable/board/useBoard';
import { BoardView, Drag, DropInfo, dropInfo, entrySectionKey, unplacedLessons } from '../../components/timetable/board/boardModel';
import MasterGrid, { MasterRow } from '../../components/timetable/board/MasterGrid';
import EntityList, { EntityItem } from '../../components/timetable/board/EntityList';
import UnplacedTray from '../../components/timetable/board/UnplacedTray';
import RunBanner from '../../components/timetable/board/RunBanner';
import EntryMenu from '../../components/timetable/board/EntryMenu';
import GenerateDialog from '../../components/timetable/board/GenerateDialog';
import ReviewDialog from '../../components/timetable/board/ReviewDialog';
import PublishDialog from '../../components/timetable/board/PublishDialog';
import HistoryDialog from '../../components/timetable/board/HistoryDialog';
import PrintMenu from '../../components/timetable/board/PrintMenu';
import BoardStart from '../../components/timetable/board/BoardStart';

const VIEWS: { value: BoardView; label: string }[] = [
  { value: 'class', label: 'Classes' },
  { value: 'teacher', label: 'Teachers' },
  { value: 'room', label: 'Rooms' },
  { value: 'master', label: 'Whole school' },
];

/** A subject's name for a compact cell: initials of a longer name, else its first letters. */
const shortName = (name: string) => {
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length > 1) return words.map((w) => w[0].toUpperCase()).join('').slice(0, 4);
  return name.length > 5 ? name.slice(0, 4) : name;
};

/** The school's timetable for the session: the live one, and the draft being built. */
const TimetablePage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const editor = canWrite('timetable');
  const { viewSession, activeSession } = useSessionContext();
  const session = viewSession ?? activeSession;
  const [params, setParams] = useSearchParams();

  const status: 'draft' | 'published' = params.get('view') === 'draft' || (!params.get('view') && editor) ? 'draft' : 'published';
  const by = (VIEWS.find((v) => v.value === params.get('by'))?.value ?? 'class') as BoardView;
  const selected = params.get('id');
  const setParam = useCallback((patch: Record<string, string | null>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => (v === null ? next.delete(k) : next.set(k, v)));
      return next;
    }, { replace: true });
  }, [setParams]);

  const { board, index, loading, reload, run, startRun, stopRun, settleReview, move, swap, place, unplace, setLocked } = useBoard(session?._id, status);
  const timetable = board?.timetable ?? null;
  const editable = editor && status === 'draft' && !!timetable && !run;

  // An editor landing with no draft but a live timetable sees the live one.
  useEffect(() => {
    if (!params.get('view') && board && !timetable && board.otherExists && status === 'draft') setParam({ view: 'published' });
  }, [board, timetable, status, params, setParam]);

  // ─── What the page shows ────────────────────────────────────────────────

  const entities: EntityItem[] = useMemo(() => {
    if (!index || !board) return [];
    if (by === 'class' || by === 'master') {
      return board.sections.map((s) => {
        const lessons = board.lessons.filter((l) => l.sectionKey === s.key);
        const required = lessons.reduce((n, l) => n + l.count, 0);
        const pending = status === 'draft' ? unplacedLessons(index, (l) => l.sectionKey === s.key).reduce((n, u) => n + u.periods, 0) : 0;
        return { id: s.key, label: s.label, detail: `${required} periods a week`, pending };
      });
    }
    if (by === 'teacher') {
      const teachers = new Map<string, { name: string; load: number }>();
      board.lessons.forEach((l) => {
        if (!l.teacherId) return;
        const cur = teachers.get(l.teacherId) ?? { name: l.teacherName ?? 'Teacher', load: 0 };
        cur.load += l.count;
        teachers.set(l.teacherId, cur);
      });
      return Array.from(teachers).sort((a, b) => a[1].name.localeCompare(b[1].name)).map(([id, t]) => ({
        id, label: t.name, detail: `${t.load} periods a week`,
        pending: status === 'draft' ? unplacedLessons(index, (l) => l.teacherId === id).reduce((n, u) => n + u.periods, 0) : 0,
      }));
    }
    return board.rooms.filter((r) => r.type !== 'classroom').map((r) => ({ id: r._id, label: r.name, detail: ROOM_TYPE_LABELS[r.type] ?? r.type }));
  }, [index, board, by, status]);

  const current = by === 'master' ? null : (entities.find((e) => e.id === selected) ?? entities[0] ?? null);

  const inView = useCallback((e: Entry) => {
    if (!current) return false;
    if (by === 'class') return entrySectionKey(e) === current.id;
    if (by === 'teacher') return e.teacherId === current.id;
    return e.roomId === current.id;
  }, [by, current]);

  // ─── Dragging ───────────────────────────────────────────────────────────

  const [drag, setDrag] = useState<Drag | null>(null);
  const endDrag = useCallback(() => setDrag(null), []);

  /** For every cell in view: what dropping the dragged card there would do. */
  const drops = useMemo(() => {
    if (!drag || !index || !timetable) return null;
    const map = new Map<string, DropInfo>();
    const shown = by === 'master'
      ? index.board.entries.filter((e) => entrySectionKey(e) === (drag.kind === 'entry' ? entrySectionKey(drag.entry) : drag.lesson.sectionKey))
      : index.board.entries.filter(inView);
    const at = new Map(shown.map((e) => [`${e.day}:${e.period}`, e]));
    for (const day of timetable.grid.days) {
      for (let period = 1; period <= 20; period += 1) {
        map.set(`${day}:${period}`, dropInfo(index, drag, day, period, at.get(`${day}:${period}`)));
      }
    }
    return map;
  }, [drag, index, timetable, by, inView]);

  const onDrop = useCallback((day: number, period: number, occupantId?: string) => {
    const d = drag;
    setDrag(null);
    if (!d || !board) return;
    const occupant = occupantId ? board.entries.find((e) => e._id === occupantId) : undefined;
    if (d.kind === 'lesson') place(d.lesson, day, period, d.span);
    else if (occupant) swap(d.entry, occupant);
    else move(d.entry, day, period);
  }, [drag, board, place, swap, move]);

  const startEntryDrag = useCallback((entry: Entry) => (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', entry._id);
    // Let the browser take its drag image before the grid recolours.
    requestAnimationFrame(() => setDrag({ kind: 'entry', entry }));
  }, []);

  const startLessonDrag = useCallback((lesson: Lesson, span: 1 | 2) => {
    if (editable) requestAnimationFrame(() => setDrag({ kind: 'lesson', lesson, span }));
  }, [editable]);

  // ─── Cards ──────────────────────────────────────────────────────────────

  const [menu, setMenu] = useState<{ anchor: HTMLElement; entry: Entry } | null>(null);

  const cardFor = useCallback((e: Entry, compact = false): React.ReactNode => {
    if (!index) return null;
    const subject = index.subjectName(e.subjectId);
    const lines = by === 'class' || by === 'master'
      ? [index.teacherName(e.teacherId), index.roomName(e.roomId)]
      : by === 'teacher'
        ? [index.sectionLabelOf(e), index.roomName(e.roomId)]
        : [index.sectionLabelOf(e), index.teacherName(e.teacherId)];
    return (
      <Tooltip title={compact ? `${subject} · ${index.teacherName(e.teacherId)}` : ''} disableInteractive>
        <Box sx={{ height: '100%' }}>
          <LessonCard
            subjectId={e.subjectId}
            title={compact ? shortName(subject) : subject}
            subtitle={compact ? undefined : lines[0] ?? undefined}
            meta={lines[1]}
            locked={status === 'draft' && e.locked}
            compact={compact}
            draggable={editable}
            onDragStart={startEntryDrag(e)}
            onDragEnd={endDrag}
            onClick={(ev) => setMenu({ anchor: ev.currentTarget, entry: e })}
          />
        </Box>
      </Tooltip>
    );
  }, [index, by, status, editable, endDrag, startEntryDrag]);

  const items: GridItem[] = useMemo(
    () => (board ? board.entries.filter(inView).map((e) => ({ id: e._id, day: e.day, period: e.period, span: e.span, node: cardFor(e) })) : []),
    [board, inView, cardFor],
  );

  const masterRows: MasterRow[] = useMemo(() => (board && by === 'master'
    ? board.sections.map((s) => ({
      key: s.key,
      label: s.label,
      items: board.entries.filter((e) => entrySectionKey(e) === s.key).map((e) => ({ id: e._id, day: e.day, period: e.period, span: e.span, node: cardFor(e, true) })),
    }))
    : []), [board, by, cardFor]);

  const tray = useMemo(() => {
    if (!index || status !== 'draft') return [];
    const filter = (l: Lesson) => {
      if (by === 'master' || !current) return true;
      if (by === 'class') return l.sectionKey === current.id;
      if (by === 'teacher') return l.teacherId === current.id;
      const room = board!.rooms.find((r) => r._id === current.id);
      return !!room && l.roomType === room.type;
    };
    return unplacedLessons(index, filter);
  }, [index, status, by, current, board]);

  // ─── Actions ────────────────────────────────────────────────────────────

  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<'generate' | 'publish' | 'history' | 'discard' | 'clear' | 'problems' | 'review' | null>(null);

  // A result held for review opens itself once.
  const reviewId = run?.status === 'review' ? run._id : null;
  useEffect(() => { if (reviewId) setDialog('review'); }, [reviewId]);
  const scopeLabel = useMemo(() => {
    if (!run?.scope?.length || !board) return undefined;
    const labels = run.scope.map((k) => board.sections.find((s) => s.key === k)?.label).filter(Boolean) as string[];
    return labels.length <= 2 ? labels.join(' and ') : `${labels.length} classes`;
  }, [run, board]);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);

  const openDraft = useCallback(async (from: 'empty' | 'published', thenGenerate = false) => {
    if (!session) return;
    setBusy(true);
    try {
      await timetableService.createDraft(session._id, from);
      setParam({ view: 'draft' });
      if (status === 'draft') await reload();
      if (thenGenerate) setDialog('generate');
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not start a draft', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }, [session, setParam, status, reload, enqueueSnackbar]);

  const publish = useCallback(async (note: string) => {
    await timetableService.publish(timetable!._id, note || undefined);
    enqueueSnackbar('Timetable published', { variant: 'success' });
    setParam({ view: 'published' });
  }, [timetable, enqueueSnackbar, setParam]);

  const discard = useCallback(async () => {
    setBusy(true);
    try {
      await timetableService.discardDraft(timetable!._id);
      setDialog(null);
      enqueueSnackbar('Draft discarded', { variant: 'success' });
      setParam({ view: 'published' });
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not discard the draft', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }, [timetable, enqueueSnackbar, setParam]);

  const clearBoard = useCallback(async () => {
    setBusy(true);
    try {
      await timetableService.clearUnlocked(timetable!._id);
      setDialog(null);
      await reload(true);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not clear the draft', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }, [timetable, reload, enqueueSnackbar]);

  // ─── Render ─────────────────────────────────────────────────────────────

  if (!session) {
    return <EmptyState icon={<EventBusy />} title="No active session" description="Start an academic session to build its timetable." />;
  }

  const placed = index?.totalPlaced ?? 0;
  const required = index?.totalRequired ?? 0;
  const lockedCount = board?.entries.filter((e) => e.locked).reduce((n, e) => n + e.span, 0) ?? 0;
  const problems = board?.problems ?? [];
  const errorCount = problems.filter((p) => p.severity === 'error').length;
  const quality = timetable?.quality;
  const printCurrent = current && by !== 'master'
    ? { view: by as PrintView, id: by === 'class' ? (board?.sections.find((s) => s.key === current.id)?.sectionInstanceId ?? board?.sections.find((s) => s.key === current.id)?.classId ?? current.id) : current.id, label: current.label }
    : null;

  const subtitle = (() => {
    if (!timetable) return session.name;
    if (status === 'published') {
      return `${session.name} · Live${timetable.publishedAt ? ` since ${new Date(timetable.publishedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}${timetable.note ? ` · ${timetable.note}` : ''}`;
    }
    return `${session.name} · Draft · edited ${formatDistanceToNow(new Date(timetable.updatedAt), { addSuffix: true })}`;
  })();

  return (
    <Box>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h4" fontWeight={700}>Timetable</Typography>
          <Typography variant="body2" color="text.secondary">{loading && !board ? <Skeleton width={260} /> : subtitle}</Typography>
        </Box>
        {board && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {editor && timetable && (
              board.otherExists ? (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={status}
                  onChange={(_, v) => v && setParam({ view: v })}
                  sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.75, fontWeight: 600 } }}
                >
                  <ToggleButton value="published">Live</ToggleButton>
                  <ToggleButton value="draft">Draft</ToggleButton>
                </ToggleButtonGroup>
              ) : status === 'published' && (
                <Button variant="outlined" startIcon={<EditOutlined />} onClick={() => openDraft('published')} disabled={busy} sx={{ textTransform: 'none' }}>
                  Edit timetable
                </Button>
              )
            )}
            {timetable && <PrintMenu sessionId={session._id} status={status} current={printCurrent} />}
            {(timetable || board.otherExists) && (
              <Tooltip title="History">
                <IconButton onClick={() => setDialog('history')} aria-label="History"><HistoryRounded /></IconButton>
              </Tooltip>
            )}
            {editable && (
              <>
                <Tooltip title="More">
                  <IconButton onClick={(e) => setMoreAnchor(e.currentTarget)} aria-label="More"><MoreHoriz /></IconButton>
                </Tooltip>
                <Button variant="outlined" startIcon={<AutoAwesomeRounded />} onClick={() => setDialog('generate')} sx={{ textTransform: 'none' }}>
                  Generate
                </Button>
                <Button variant="contained" onClick={() => setDialog('publish')} sx={{ textTransform: 'none' }}>Publish</Button>
              </>
            )}
          </Stack>
        )}
      </Stack>

      {loading && !board ? (
        <Skeleton variant="rounded" height={420} sx={{ borderRadius: '16px' }} />
      ) : !board ? null : !timetable ? (
        status === 'draft' || !board.otherExists ? (
          <BoardStart
            sessionName={session.name}
            sessionId={session._id}
            problems={problems}
            canWrite={editor}
            busy={busy}
            onGenerate={() => openDraft('empty', true)}
            onManual={() => openDraft('empty')}
          />
        ) : (
          <EmptyState
            icon={<EventBusy />}
            title="Not published yet"
            description="The timetable is still a draft."
            action={editor ? { label: 'Open the draft', onClick: () => setParam({ view: 'draft' }) } : undefined}
          />
        )
      ) : (
        <>
          {run && <RunBanner run={run} canStop={editor} onStop={stopRun} scopeLabel={scopeLabel} onReview={() => setDialog('review')} />}

          {/* View switch and summary */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={by}
              onChange={(_, v) => v && setParam({ by: v, id: null })}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.75, fontWeight: 600 } }}
            >
              {VIEWS.map((v) => <ToggleButton key={v.value} value={v.value}>{v.label}</ToggleButton>)}
            </ToggleButtonGroup>
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
              {status === 'draft' && (
                <ToneChip tone={placed >= required ? 'success' : 'warning'} label={`${placed} of ${required} periods placed`} />
              )}
              {quality && (
                <>
                  <ToneChip tone={quality.sectionGaps ? 'warning' : 'success'} label={quality.sectionGaps ? `${quality.sectionGaps} gap${quality.sectionGaps === 1 ? '' : 's'} in class days` : 'No gaps in class days'} />
                  <ToneChip tone="neutral" label={`${quality.teacherGaps} teacher gap${quality.teacherGaps === 1 ? '' : 's'}`} />
                </>
              )}
              {status === 'draft' && problems.length > 0 && (
                <Button
                  size="small"
                  color={errorCount ? 'error' : 'warning'}
                  startIcon={<Badge badgeContent={problems.length} color={errorCount ? 'error' : 'warning'}><ReportProblemOutlined fontSize="small" /></Badge>}
                  onClick={() => setDialog('problems')}
                  sx={{ textTransform: 'none', ml: 0.5 }}
                >
                  Problems
                </Button>
              )}
            </Stack>
          </Stack>

          {/* Board */}
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: by === 'master' ? '1fr' : '200px 1fr' } }}>
            {by !== 'master' && (
              <Paper variant="outlined" sx={{ borderRadius: '14px', p: 1.25, position: { lg: 'sticky' }, top: 72, alignSelf: 'start', maxHeight: { lg: 'calc(100vh - 96px)' }, display: 'flex', flexDirection: 'column' }}>
                <EntityList
                  items={entities}
                  selected={current?.id ?? null}
                  onSelect={(id) => setParam({ id })}
                  placeholder={by === 'class' ? 'Find a class' : by === 'teacher' ? 'Find a teacher' : 'Find a room'}
                />
              </Paper>
            )}

            <Box sx={{ minWidth: 0 }}>
              {status === 'draft' && (
                <Paper
                  variant="outlined"
                  onDragOver={(e) => { if (drag?.kind === 'entry' && editable) e.preventDefault(); }}
                  onDrop={(e) => { e.preventDefault(); const d = drag; setDrag(null); if (d?.kind === 'entry') unplace(d.entry); }}
                  sx={(theme) => ({
                    mb: 1.5, px: 1.5, py: 1.25, borderRadius: '12px',
                    borderStyle: drag?.kind === 'entry' ? 'dashed' : 'solid',
                    borderColor: drag?.kind === 'entry' ? 'primary.main' : 'divider',
                    bgcolor: drag?.kind === 'entry' ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                  })}
                >
                  {tray.length > 0 && (
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 0.75 }}>
                      To place · {tray.reduce((n, t) => n + t.periods, 0)}
                    </Typography>
                  )}
                  <UnplacedTray
                    items={tray}
                    showClass={by !== 'class'}
                    onDragStart={startLessonDrag}
                    onDragEnd={endDrag}
                  />
                </Paper>
              )}
              {by === 'master' ? (
                <MasterGrid
                  grid={timetable.grid}
                  rows={masterRows}
                  drops={drag && drops ? { rowKey: drag.kind === 'entry' ? entrySectionKey(drag.entry) : drag.lesson.sectionKey, map: drops } : null}
                  onDrop={onDrop}
                  onRowClick={(key) => setParam({ by: 'class', id: key })}
                />
              ) : current ? (
                <WeekGrid grid={timetable.grid} items={items} drops={drops} onDrop={onDrop} />
              ) : (
                <EmptyState
                  icon={<EventBusy />}
                  title={by === 'room' ? 'No special rooms' : 'Nothing to show'}
                  description={by === 'room' ? 'Labs and other rooms appear here once added in Setup.' : undefined}
                />
              )}
            </Box>
          </Box>
        </>
      )}

      {/* Menus and dialogs */}
      {index && (
        <EntryMenu
          anchor={menu?.anchor ?? null}
          entry={menu?.entry ?? null}
          index={index}
          editable={editable}
          onClose={() => setMenu(null)}
          onLock={(e, locked) => setLocked([e], locked)}
          onUnplace={unplace}
          onShowTeacher={(id) => setParam({ by: 'teacher', id })}
          onShowClass={(e) => setParam({ by: 'class', id: entrySectionKey(e) })}
        />
      )}
      <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)} slotProps={{ paper: { sx: { borderRadius: '12px', minWidth: 220 } } }}>
        <MenuItem onClick={() => { setMoreAnchor(null); setDialog('clear'); }}>
          <ListItemText primary="Clear the week" secondary="Keeps locked periods" />
        </MenuItem>
        <MenuItem onClick={() => { setMoreAnchor(null); setDialog('discard'); }}>
          <ListItemText primary="Discard draft" primaryTypographyProps={{ color: 'error.main' }} />
        </MenuItem>
      </Menu>

      {board && session && (
        <>
          <GenerateDialog
            open={dialog === 'generate'}
            onClose={() => setDialog(null)}
            onStart={startRun}
            entries={board.entries}
            sections={board.sections}
            currentSection={by === 'class' ? current?.id ?? null : null}
            problems={problems}
            sessionId={session._id}
          />
          {run?.status === 'review' && timetable && (
            <ReviewDialog
              open={dialog === 'review'}
              run={run}
              grid={timetable.grid}
              scopeLabel={scopeLabel ?? 'the chosen classes'}
              onClose={() => setDialog(null)}
              onDecide={async (apply) => { await settleReview(apply); setDialog(null); }}
            />
          )}
          <PublishDialog
            open={dialog === 'publish'}
            onClose={() => setDialog(null)}
            onPublish={publish}
            placed={placed}
            required={required}
            replacesLive={board.otherExists}
          />
          <HistoryDialog
            open={dialog === 'history'}
            onClose={() => setDialog(null)}
            sessionId={session._id}
            canRestore={editor}
            hasDraft={status === 'draft' ? !!timetable : board.otherExists}
            onRestored={() => { setParam({ view: 'draft' }); if (status === 'draft') reload(); }}
          />
          <ConfirmationDialog
            open={dialog === 'discard'}
            onClose={() => setDialog(null)}
            onConfirm={discard}
            title="Discard this draft?"
            message="Everything in the draft is deleted. The live timetable is not affected."
            confirmText="Discard"
            loading={busy}
          />
          <ConfirmationDialog
            open={dialog === 'clear'}
            onClose={() => setDialog(null)}
            onConfirm={clearBoard}
            title="Clear the week?"
            message={`Every period except the ${lockedCount} locked go back to unplaced.`}
            confirmText="Clear"
            loading={busy}
          />
          <Dialog open={dialog === 'problems'} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>Problems</Box>
              <IconButton size="small" onClick={() => setDialog(null)} aria-label="Close"><Close fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Errors stop generation; warnings are worth a look.</Typography>
              <ProblemList problems={problems} sessionId={session._id} hereFix="board" />
            </DialogContent>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default TimetablePage;
